import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import FormItem from '@lib/components/ReactHookForm/FormItem';
import Input from '@lib/components/Input/Input';
import { InputSizes, InputTypes } from '@lib/components/Input/enums';
import { Values } from '@lib/interfaces/form';
import { QUESTION_NUMBER_PRECISION } from '@lib/enums/form';
import {
  FormTemplateQuestionFieldOptionConditionEnum,
  FormTemplateQuestionFieldOptionGroupEnum,
  FormTemplateQuestionFieldsKindEnum,
} from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import { onKeyDownFn } from '@lib/utils/formOptionsHelpers';
import { fieldLabelByCondition } from '../constants';
import styles from './QuestionFieldOption.module.scss';

interface Props {
  isFocused?: boolean;
  kind: FormTemplateQuestionFieldsKindEnum;
  parentGroupIndex: number;
  parentIndex: number;
  parentName: string;
  parentValue: Values[];
  onAdd: (v: Values) => void;
  onRemove: () => void;
}

function QuestionFieldOptionFormItem(props: Props) {
  const {
    isFocused,
    kind,
    onAdd,
    onRemove,
    parentGroupIndex,
    parentIndex,
    parentName,
    parentValue,
  } = props;
  const { t } = useTranslation();
  const optionValues = _get(
    parentValue,
    [parentIndex, FormTemplateFieldNames.Values],
    [],
  ) as string[];
  const groupValue = _get(
    parentValue,
    [parentIndex, FormTemplateFieldNames.Group],
    '',
  ) as FormTemplateQuestionFieldOptionGroupEnum;
  const conditionValue = _get(
    parentValue,
    [parentIndex, FormTemplateFieldNames.Condition],
    '',
  ) as FormTemplateQuestionFieldOptionConditionEnum;

  const getLabelText = () => {
    if (groupValue === FormTemplateQuestionFieldOptionGroupEnum.Columns)
      return t('column');
    if (groupValue === FormTemplateQuestionFieldOptionGroupEnum.Rows)
      return t('row');
    return t('option');
  };

  const getLabel = () => {
    if (conditionValue) return t(fieldLabelByCondition[conditionValue]);
    if (kind === FormTemplateQuestionFieldsKindEnum.ImportantNotice)
      return t('important-notice-message');
    if (kind === FormTemplateQuestionFieldsKindEnum.DecisionBox)
      return t('decision-box-message');
    if (parentGroupIndex !== undefined)
      return `${getLabelText()} ${parentGroupIndex + 1}`;
    return `${getLabelText()} ${parentIndex + 1}`;
  };

  if (kind === FormTemplateQuestionFieldsKindEnum.YesNo) {
    return (
      <div className={styles.yesNoFieldOption}>
        <Button
          buttonType={ButtonTypes.primaryOutlined}
          buttonText={optionValues[0]}
          buttonSize={ButtonSizes.large}
          leftIcon={
            groupValue === FormTemplateQuestionFieldOptionGroupEnum.Yes
              ? 'check_circle'
              : 'cancel'
          }
          leftIconClassName={materialSymbolsOutlined}
          fullWidth
          disabled
        />
      </div>
    );
  }
  if (conditionValue === FormTemplateQuestionFieldOptionConditionEnum.Between) {
    return (
      <div className={styles.conditionFields}>
        <FormItem
          name={`${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`}
          label={t(fieldLabelByCondition[conditionValue])}
          element={Input}
          componentProps={{
            type: InputTypes.number,
            name: `${parentName}.${parentIndex}.values.0`,
            precision: QUESTION_NUMBER_PRECISION,
          }}
          formItemClassName={styles.formItem}
        />
        <div className={styles.conditionFieldsSeparator}>
          <Typography variant="body" strong>
            -
          </Typography>
        </div>
        <FormItem
          name={`${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.1`}
          label={t(fieldLabelByCondition[conditionValue])}
          element={Input}
          componentProps={{
            type: InputTypes.number,
            name: `${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.1`,
            precision: QUESTION_NUMBER_PRECISION,
          }}
          formItemClassName={styles.formItem}
        />
      </div>
    );
  }
  if (conditionValue) {
    const componentProps = {
      name: `${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`,
      precision: QUESTION_NUMBER_PRECISION,
      type: InputTypes.number,
    };
    return (
      <FormItem
        componentProps={componentProps}
        element={Input}
        formItemClassName={styles.formItem}
        label={getLabel()}
        name={`${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`}
      />
    );
  }
  if (kind === FormTemplateQuestionFieldsKindEnum.Checklist) {
    const isDeleteBtnVisible = parentValue?.length > 1;
    const componentProps = {
      autoFocus: isFocused,
      hasBorder: false,
      hasErrorAsTooltip: true,
      name: `${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`,
      onKeyDown: (e) =>
        onKeyDownFn(e, {
          onAdd,
          onRemove: isDeleteBtnVisible ? onRemove : undefined,
          index: parentIndex,
        }),
      placeholder: t('checklist-option-placeholder'),
      size: InputSizes.medium,
    };
    return (
      <FormItem
        componentProps={componentProps}
        element={Input}
        formItemClassName={styles.formItem}
        hideErrors
        name={`${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`}
      />
    );
  }
  if (kind === FormTemplateQuestionFieldsKindEnum.CheckboxButtonGrid) {
    const componentProps = {
      hasBorder: false,
      hasErrorAsTooltip: true,
      name: `${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`,
      placeholder: t('input-label-here'),
      resize: true,
      type: InputTypes.textarea,
    };
    return (
      <FormItem
        componentProps={componentProps}
        element={Input}
        formItemClassName={styles.formItem}
        hideErrors
        name={`${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`}
      />
    );
  }
  const componentProps = {
    name: `${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`,
  };
  return (
    <FormItem
      componentProps={componentProps}
      element={Input}
      formItemClassName={styles.formItem}
      label={getLabel()}
      name={`${parentName}.${parentIndex}.${FormTemplateFieldNames.Values}.0`}
    />
  );
}

export default memo(QuestionFieldOptionFormItem);
